import * as React from 'react';
import { Box, Button, FormHelperText } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { InputProps, useInput, useTranslate } from 'react-admin';
import { useFormContext, useFormState } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { ScheduleTaskEditor } from './ScheduleTaskEditor';
import { createEmptyScheduleTask } from './scheduleTaskDefaults';
import { isScheduleTaskSameAsPrevious } from './scheduleTaskComparison';
import { ScheduleTaskForm } from './types';
import { useTaskTemplates } from './useTaskTemplates';

const buildInitialExpandedIds = (tasks: ScheduleTaskForm[]): Set<string> => {

  const expanded = new Set<string>();

  for (let index = 0; index < tasks.length; index += 1) {

    const task = tasks[index];
    const sameAsPrevious = isScheduleTaskSameAsPrevious(task, tasks[index - 1]);
    if (!sameAsPrevious) {

      expanded.add(task.id);

    }

  }

  return expanded;

};

export const ScheduleTasksInput = (props: InputProps) => {

  const { field } = useInput(props);
  const { getValues } = useFormContext();
  const translate = useTranslate();
  const { errors, isSubmitted } = useFormState();
  const { taskTemplates } = useTaskTemplates();
  const tasks: ScheduleTaskForm[] = React.useMemo(() => field.value || [], [field.value]);
  const canRemoveTasks = tasks.length > 1;
  const scheduleTasksError = errors.scheduleTasks?.message as string | undefined;
  const showScheduleTasksError = tasks.length === 0 && (isSubmitted || scheduleTasksError);
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(() => buildInitialExpandedIds(tasks));
  const [stickyExpandedIds, setStickyExpandedIds] = React.useState<Set<string>>(() => new Set());
  const [activeTaskId, setActiveTaskId] = React.useState<string | null>(null);

  const getCurrentTasks = React.useCallback((): ScheduleTaskForm[] => (
    field.value || (getValues(props.source) as ScheduleTaskForm[] | undefined) || []
  ), [field.value, getValues, props.source]);

  React.useEffect(() => {

    setExpandedIds((prev) => {

      const next = new Set(prev);
      const currentIds = new Set(tasks.map((task) => task.id));

      for (const id of prev) {

        if (!currentIds.has(id)) {

          next.delete(id);

        }

      }

      tasks.forEach((task, index) => {

        if (index === 0) return;

        const sameAsPrevious = isScheduleTaskSameAsPrevious(task, tasks[index - 1]);
        if (
          sameAsPrevious
          && !stickyExpandedIds.has(task.id)
          && task.id !== activeTaskId
        ) {

          next.delete(task.id);

        }

      });

      return next;

    });

  }, [tasks, stickyExpandedIds, activeTaskId]);

  const updateTasks = (newTasks: ScheduleTaskForm[]) => {

    field.onChange(newTasks);

  };

  const updateTask = (taskId: string, task: ScheduleTaskForm) => {

    const currentTasks = getCurrentTasks();
    const newTasks = currentTasks.map((currentTask) => (
      currentTask.id === taskId ? task : currentTask
    ));
    updateTasks(newTasks);

  };

  const addTask = () => {

    const newTask = createEmptyScheduleTask();

    updateTasks([...getCurrentTasks(), newTask]);
    setExpandedIds((prev) => new Set([...prev, newTask.id]));
    setActiveTaskId(newTask.id);
    setStickyExpandedIds(new Set([newTask.id]));

  };

  const removeTask = (taskId: string) => {

    if (!canRemoveTasks) return;

    // eslint-disable-next-line no-alert
    if (!window.confirm(translate('resources.process-templates.text.remove_step_confirm'))) return;

    updateTasks(getCurrentTasks().filter((task) => task.id !== taskId));

    setExpandedIds((prev) => {

      const next = new Set(prev);
      next.delete(taskId);
      return next;

    });
    setStickyExpandedIds((prev) => {

      const next = new Set(prev);
      next.delete(taskId);
      return next;

    });
    setActiveTaskId((current) => (current === taskId ? null : current));

  };

  const duplicateTask = (taskId: string) => {

    const currentTasks = getCurrentTasks();
    const index = currentTasks.findIndex((task) => task.id === taskId);
    if (index === -1) return;

    const copy = JSON.parse(JSON.stringify(currentTasks[index])) as ScheduleTaskForm;
    copy.id = uuidv4();
    const newTasks = [...currentTasks];
    newTasks.splice(index + 1, 0, copy);
    updateTasks(newTasks);

    const sameAsPrevious = isScheduleTaskSameAsPrevious(copy, currentTasks[index]);
    if (sameAsPrevious) {

      setExpandedIds((prev) => {

        const next = new Set(prev);
        next.delete(copy.id);
        return next;

      });
      setStickyExpandedIds(new Set());
      setActiveTaskId(null);

    } else {

      setExpandedIds((prev) => new Set([...prev, copy.id]));
      setStickyExpandedIds(new Set([copy.id]));
      setActiveTaskId(copy.id);

    }

  };

  const handleTaskActivate = (taskId: string) => {

    setActiveTaskId((currentActiveId) => {

      if (currentActiveId !== taskId) {

        setStickyExpandedIds(new Set());

      }
      return taskId;

    });

  };

  const handleExpandedChange = (taskId: string, expanded: boolean, sameAsPrevious: boolean) => {

    setExpandedIds((prev) => {

      const next = new Set(prev);
      if (expanded) {

        next.add(taskId);

      } else {

        next.delete(taskId);

      }
      return next;

    });

    setStickyExpandedIds((prev) => {

      const next = new Set(prev);
      if (expanded && sameAsPrevious) {

        next.add(taskId);

      } else {

        next.delete(taskId);

      }
      return next;

    });

    if (expanded) {

      setActiveTaskId(taskId);

    }

  };

  return (
    <Box>
      {tasks.map((task, index) => (
        <ScheduleTaskEditor
          key={task.id}
          task={task}
          index={index}
          taskTemplates={taskTemplates}
          expanded={expandedIds.has(task.id)}
          isSameAsPrevious={isScheduleTaskSameAsPrevious(task, tasks[index - 1])}
          onExpandedChange={(expanded) => handleExpandedChange(
            task.id,
            expanded,
            isScheduleTaskSameAsPrevious(task, tasks[index - 1]),
          )}
          onActivate={() => handleTaskActivate(task.id)}
          onChange={(updated) => updateTask(task.id, updated)}
          onRemove={() => removeTask(task.id)}
          onDuplicate={() => duplicateTask(task.id)}
          canRemove={canRemoveTasks}
        />
      ))}
      {showScheduleTasksError && (
        <FormHelperText error sx={{ mb: 1 }}>
          {translate(scheduleTasksError || 'resources.process-templates.text.schedule_tasks_required', {
            _: 'At least one schedule task is required',
          })}
        </FormHelperText>
      )}
      <Button startIcon={<AddIcon />} onClick={addTask} variant="outlined">
        {translate('resources.process-templates.text.add_step')}
      </Button>
    </Box>
  );

};
